import React, { FC, useContext, createContext, useReducer } from 'react';
import { Option, TableRow } from '@code/innovance-react-ui-kit';

enum ACTIONS {
  SEARCH_OPERATION,
  CLEAR,
}

type Action =
  | {
      type: ACTIONS.SEARCH_OPERATION;
      payload: {
        searchFormValues: OperationSearchValues;
      };
    }
  | { type: ACTIONS.CLEAR };

interface OperationSearchValues {
  beginDate: Date | null;
  endDate: Date | null;
  product: Option | null;
  productOperation: Option | null;
  referenceNumber: string;
  operationStatus: Option | null;
  amountFrom: string;
  amountTo: string;

  criteria1: string;
  criteria2: string;
  criteria3: string;
  criteria4: string;
  criteria5: string;
  criteria6: string;

  operationPageNumber: number;
  selectedOperation: TableRow;
}

interface ExecutedOperationState {
  searchFormValues: OperationSearchValues;
}

const initialValues = {
  searchFormValues: {
    beginDate: null,
    endDate: null,
    product: null,
    productOperation: null,
    referenceNumber: '',
    operationStatus: null,
    amountFrom: '',
    amountTo: '',

    criteria1: '',
    criteria2: '',
    criteria3: '',
    criteria4: '',
    criteria5: '',
    criteria6: '',

    selectedOperation: {
      id: '',
      columns: [],
    },
    operationPageNumber: 1,
  },
};

type ExecutedOperationDispatch = (action: Action) => void;

const ExecutedOperationStateContext = createContext<ExecutedOperationState | undefined>(undefined);
const ExecutedOperationDispatchContext = createContext<ExecutedOperationDispatch | undefined>(
  undefined
);

const executedOperationReducer = (
  state: ExecutedOperationState,
  action: Action
): ExecutedOperationState => {
  switch (action.type) {
    case ACTIONS.SEARCH_OPERATION:
      return {
        ...state,
        ...action.payload,
      };

    case ACTIONS.CLEAR:
      return initialValues;
    default:
      return state;
  }
};

const ExecutedOperationProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(executedOperationReducer, initialValues);
  return (
    <ExecutedOperationStateContext.Provider value={state}>
      <ExecutedOperationDispatchContext.Provider value={dispatch}>
        {children}
      </ExecutedOperationDispatchContext.Provider>
    </ExecutedOperationStateContext.Provider>
  );
};

export const useExecutedOperationState = (): ExecutedOperationState => {
  const context = useContext(ExecutedOperationStateContext);
  if (context === undefined) {
    throw new Error('useExecutedOperationState must be used within a ExecutedOperationProvider');
  }

  return context;
};

type SearchInput = Pick<ExecutedOperationState, 'searchFormValues'>;

export const useOperationSearchDispatch = (): ((payload: SearchInput) => void) => {
  const dispatch = useContext(ExecutedOperationDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useOperationSearchDispatch must be used within a ExecutedOperationProvider');
  }

  return (payload: SearchInput) => dispatch({ type: ACTIONS.SEARCH_OPERATION, payload });
};

export const useClearDispatch = (): (() => void) => {
  const dispatch = useContext(ExecutedOperationDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClearDispatch must be used within a ExecutedOperationProvider');
  }

  return () => dispatch({ type: ACTIONS.CLEAR });
};

export default ExecutedOperationProvider;
